// Main Types Export for 1vX.gg
// Centralized export of all type definitions

import type {
  Article,
  GameArticle,
  ESportsArticle,
  ReviewArticle,
} from './content';

// Content types
export * from './content';

// API types
export * from './api';

// Search types
export * from './search';

// Re-export commonly used types for convenience
export type {
  // Content types
  Article,
  GameArticle,
  ESportsArticle,
  ReviewArticle,
  Game,
  Streamer,
  Author,

  // Collection types
  ArticleCollection,
  GameCollection,
  StreamerCollection,

  // Response types
  NewsIndexResponse,
  GamesIndexResponse,
  ReviewsIndexResponse,
  GameResponse,
  StreamerResponse,

  // Model types
  NewsIndexModel,
  GamesIndexModel,
  GameModel,
  StreamerModel,

  // Query variables
  SlugQueryVariables,
  GameSlugQueryVariables,
  StreamerSlugQueryVariables,

  // API types
  APIResponse,
  APIError,
  QueryState,
  HTTPStatus,
  HTTPError,

  // Search types
  SearchResult,
  SearchResponse,
  SearchRequest,
  SearchFilters,
} from './content';

export type {
  // Service types
  DataService,
  CacheService,
  MetricsService,

  // Auth types
  AuthUser,
  AuthState,
  UserRole,

  // Pagination
  PaginatedResponse,
  PaginationParams,

  // Configuration
  AppConfig,
  EnvironmentConfig,
  Environment,
} from './api';

// Type guards for content types
export const isGameArticle = (article: Article): article is GameArticle => {
  return article.__typename === 'GameArticle';
};

export const isESportsArticle = (
  article: Article,
): article is ESportsArticle => {
  return article.__typename === 'ESportsArticle';
};

export const isReviewArticle = (article: Article): article is ReviewArticle => {
  return article.__typename === 'ReviewArticle';
};

// Utility types
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type OptionalExcept<T, K extends keyof T> = Partial<T> & Pick<T, K>;

export type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never;
}[keyof T];

// Common utility functions
export const isDefined = <T>(value: T | undefined | null): value is T => {
  return value !== undefined && value !== null;
};

export const isNotEmpty = <T>(array: T[] | undefined | null): array is T[] => {
  return isDefined(array) && array.length > 0;
};

export const hasProperty = <T extends object, K extends string>(
  obj: T,
  prop: K,
): obj is T & Record<K, unknown> => {
  return prop in obj;
};

// Error types for the application
export class ApplicationError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public metadata?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApplicationError';
  }
}

export class ValidationError extends ApplicationError {
  constructor(
    message: string,
    public field?: string,
  ) {
    super(message, 'VALIDATION_ERROR', 400);
  }
}

export class NotFoundError extends ApplicationError {
  constructor(resource: string, id?: string) {
    const message = id
      ? `${resource} with id ${id} not found`
      : `${resource} not found`;
    super(message, 'NOT_FOUND', 404);
  }
}

export class UnauthorizedError extends ApplicationError {
  constructor(message: string = 'Unauthorized') {
    super(message, 'UNAUTHORIZED', 401);
  }
}

export class ForbiddenError extends ApplicationError {
  constructor(message: string = 'Forbidden') {
    super(message, 'FORBIDDEN', 403);
  }
}

export class RateLimitError extends ApplicationError {
  constructor(message: string = 'Rate limit exceeded') {
    super(message, 'RATE_LIMIT', 429);
  }
}
